// Путь: D:\QR\QR-WEB\dashboard\src\components\Business\BusinessCreateVoucher.jsx
// Название: Business Create Voucher - DARY
// Версия: 1.1.0 - Fixed amount format + Download ZIP
// Дата: 03.12.2025

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import VoucherCard from '../User/VoucherCard';
import './BusinessVoucher.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://qr-money.onrender.com';

// Бизнес суммы: больше диапазон
const AMOUNTS = [10, 25, 50, 100, 250, 500, 1000];

// Все темы + бизнес-темы
const THEMES = [
  { id: 'default', title: 'Обычный', emoji: '🎫' },
  { id: 'birthday', title: 'День рождения', emoji: '🎂' },
  { id: 'love', title: 'С любовью', emoji: '❤️' },
  { id: 'thanks', title: 'Спасибо', emoji: '🙏' },
  { id: 'gift', title: 'Подарок', emoji: '🎁' },
  { id: 'congrats', title: 'Поздравляю', emoji: '🎉' },
  { id: 'coffee', title: 'На кофе', emoji: '☕' },
  { id: 'lunch', title: 'На обед', emoji: '🍽️' },
  { id: 'travel', title: 'Путешествие', emoji: '✈️' },
  { id: 'gaming', title: 'На игры', emoji: '🎮' },
  { id: 'shopping', title: 'Шоппинг', emoji: '🛍️' },
  { id: 'newyear', title: 'Новый год', emoji: '🎄' },
  // Бизнес-темы
  { id: 'corporate', title: 'Корпоративный', emoji: '🏢', business: true },
  { id: 'employee', title: 'Сотруднику', emoji: '👔', business: true },
  { id: 'partner', title: 'Партнёру', emoji: '🤝', business: true },
  { id: 'client', title: 'Клиенту', emoji: '⭐', business: true },
  { id: 'bonus', title: 'Бонус', emoji: '💰', business: true },
  { id: 'promo', title: 'Промо', emoji: '🎯', business: true },
];

// Расширенные сроки для бизнеса
const TTL_OPTIONS = [
  { value: '1', label: '1 час' },
  { value: '12', label: '12 часов' },
  { value: '24', label: '24 часа' },
  { value: '72', label: '3 дня' },
  { value: '168', label: '7 дней' },
  { value: '336', label: '14 дней' },
  { value: '720', label: '30 дней' },
];

const BusinessCreateVoucher = () => {
  const [amount, setAmount] = useState(null);
  const [ttlHours, setTtlHours] = useState('168');
  const [selectedTheme, setSelectedTheme] = useState(THEMES[0]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const [balance, setBalance] = useState(0);
  
  // Массовое создание
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkCount, setBulkCount] = useState(5);
  const [bulkResult, setBulkResult] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const token = localStorage.getItem('authToken');
  const userId = localStorage.getItem('userId');

  useEffect(() => {
    fetchBalance();
  }, []);

  const fetchBalance = async () => {
    try {
      const res = await axios.get(API_URL + '/api/balance/' + userId, {
        headers: { Authorization: 'Bearer ' + token }
      });
      if (res.data.success) {
        const bal = res.data.data;
        setBalance(parseFloat(bal.available_amount || bal.available || bal.amount || 0));
      }
    } catch (err) {
      console.error('Balance error:', err);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(null);
    setBulkResult(null);
    setLoading(true);

    if (!amount) {
      setError('Выберите сумму подарка');
      setLoading(false);
      return;
    }

    const totalAmount = bulkMode ? amount * bulkCount : amount;
    
    if (totalAmount > balance) {
      setError(`Недостаточно средств. Нужно: ${totalAmount} DAR`);
      setLoading(false);
      return;
    }

    try {
      if (bulkMode) {
        // Массовое создание
        const res = await axios.post(API_URL + '/api/vouchers/bulk-create', {
          amount: amount,
          ttlHours: parseInt(ttlHours),
          themeName: selectedTheme.id,
          count: bulkCount
        }, {
          headers: { Authorization: 'Bearer ' + token }
        });

        if (res.data.success) {
          setBulkResult({
            vouchers: res.data.data.vouchers,
            totalAmount: res.data.data.totalAmount,
            count: res.data.data.count,
            themeTitle: selectedTheme.title
          });
          setAmount(null);
          fetchBalance();
        } else {
          setError(res.data.error || 'Ошибка массового создания');
        }
      } else {
        // Одиночное создание
        const res = await axios.post(API_URL + '/api/vouchers/create', {
          amount: amount,
          ttlHours: parseInt(ttlHours),
          templateId: null,
          themeName: selectedTheme.id
        }, {
          headers: { Authorization: 'Bearer ' + token }
        });

        if (res.data.success) {
          setSuccess({
            ...res.data.data,
            themeId: selectedTheme.id,
            themeTitle: selectedTheme.title
          });
          setAmount(null);
          fetchBalance();
        } else {
          setError(res.data.error || 'Ошибка создания подарка');
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка сервера');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSuccess(null);
    setBulkResult(null);
    setSelectedTheme(THEMES[0]);
    setBulkMode(false);
  };

  const downloadAllQR = async () => {
    if (!bulkResult?.vouchers || bulkResult.vouchers.length === 0) return;
    
    setDownloading(true);
    
    try {
      // Создаём CSV со списком ваучеров
      const csvRows = [
        'Код,Сумма,Ссылка,Токен'
      ];
      
      bulkResult.vouchers.forEach(v => {
        csvRows.push(`${v.fallback_code},${parseFloat(v.amount).toFixed(2)} DAR,https://dary.one/v/${v.voucher_token},${v.voucher_token}`);
      });
      
      const csvContent = '\ufeff' + csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `dary_vouchers_${bulkResult.count}x${parseFloat(bulkResult.vouchers[0]?.amount || 0).toFixed(0)}usdt_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
    } catch (err) {
      console.error('Download error:', err);
      alert('Ошибка скачивания');
    } finally {
      setDownloading(false);
    }
  };

  const copyAllLinks = () => {
    if (!bulkResult?.vouchers) return;
    
    const links = bulkResult.vouchers
      .map(v => `https://dary.one/v/${v.voucher_token}`)
      .join('\n');
    
    navigator.clipboard.writeText(links);
    alert(`Скопировано ${bulkResult.vouchers.length} ссылок`);
  };

  return (
    <div className="biz-voucher-container">
      {success ? (
        <div className="biz-success-section">
          <h2>✅ Подарок создан!</h2>
          <p className="biz-success-subtitle">Отправьте эту карточку получателю</p>
          
          <VoucherCard 
            amount={success.amount}
            code={success.fallback_code}
            templateTitle={success.themeTitle}
            voucherToken={success.voucher_token}
          />

          <button onClick={resetForm} className="biz-btn-create-another">
            ➕ Создать ещё
          </button>
        </div>
      ) : bulkResult ? (
        <div className="biz-success-section">
          <h2>✅ Создано {bulkResult.count} подарков!</h2>
          <p className="biz-success-subtitle">
            На сумму {bulkResult.totalAmount} DAR • Тема: {bulkResult.themeTitle}
          </p>
          
          <div className="biz-bulk-summary">
            <div className="biz-bulk-stat">
              <span className="biz-bulk-stat-value">{bulkResult.count}</span>
              <span className="biz-bulk-stat-label">Подарков</span>
            </div>
            <div className="biz-bulk-stat">
              <span className="biz-bulk-stat-value">{parseFloat(bulkResult.vouchers[0]?.amount || 0).toFixed(2)}</span>
              <span className="biz-bulk-stat-label">DAR каждый</span>
            </div>
            <div className="biz-bulk-stat">
              <span className="biz-bulk-stat-value">{bulkResult.totalAmount}</span>
              <span className="biz-bulk-stat-label">DAR всего</span>
            </div>
          </div>

          <div className="biz-bulk-actions">
            <button onClick={downloadAllQR} className="biz-btn-download" disabled={downloading}>
              {downloading ? '⏳ Скачивание...' : '📥 Скачать CSV'}
            </button>
            <button onClick={copyAllLinks} className="biz-btn-copy">
              📋 Копировать ссылки
            </button>
            <button onClick={resetForm} className="biz-btn-create-another">
              ➕ Создать ещё
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleCreate} className="biz-voucher-form">
          {/* Header */}
          <div className="biz-form-header">
            <h2>🎁 Создать подарок</h2>
            <span className="biz-balance-badge">Баланс: <strong>{balance.toFixed(2)} DAR</strong></span>
          </div>

          {error && <div className="biz-error-message">{error}</div>}

          {/* Режим создания */}
          <div className="biz-form-group">
            <label>Режим создания</label>
            <div className="biz-mode-toggle">
              <button 
                type="button"
                className={'biz-mode-btn ' + (!bulkMode ? 'active' : '')}
                onClick={() => setBulkMode(false)}
              >
                🎫 Один подарок
              </button>
              <button 
                type="button"
                className={'biz-mode-btn ' + (bulkMode ? 'active' : '')}
                onClick={() => setBulkMode(true)}
              >
                📦 Массовое создание
              </button>
            </div>
          </div>

          {/* Количество (для bulk) */}
          {bulkMode && (
            <div className="biz-form-group">
              <label>Количество подарков (до 100)</label>
              <div className="biz-bulk-count">
                <button type="button" onClick={() => setBulkCount(Math.max(1, bulkCount - 1))}>−</button>
                <input 
                  type="number" 
                  value={bulkCount} 
                  onChange={(e) => setBulkCount(Math.min(100, Math.max(1, parseInt(e.target.value) || 1)))}
                  min="1"
                  max="100"
                />
                <button type="button" onClick={() => setBulkCount(Math.min(100, bulkCount + 1))}>+</button>
              </div>
              <div className="biz-bulk-presets">
                {[5, 10, 25, 50, 100].map(n => (
                  <button 
                    key={n} 
                    type="button" 
                    className={'biz-preset-btn ' + (bulkCount === n ? 'active' : '')}
                    onClick={() => setBulkCount(n)}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* СУММА */}
          <div className="biz-form-group">
            <label>Сумма {bulkMode ? 'каждого подарка' : 'подарка'} (DAR)</label>
            <div className="biz-amount-buttons">
              {AMOUNTS.map(val => {
                const totalNeeded = bulkMode ? val * bulkCount : val;
                const disabled = totalNeeded > balance;
                return (
                  <button 
                    key={val} 
                    type="button" 
                    className={'biz-amount-btn ' + (amount === val ? 'active' : '')}
                    onClick={() => setAmount(val)}
                    disabled={disabled}
                  >
                    {val}
                  </button>
                );
              })}
            </div>
            {bulkMode && amount && (
              <p className="biz-total-info">
                Итого: <strong>{amount * bulkCount} DAR</strong> за {bulkCount} подарков
              </p>
            )}
          </div>

          {/* СРОК ДЕЙСТВИЯ */}
          <div className="biz-form-group">
            <label>Срок действия</label>
            <div className="biz-ttl-buttons">
              {TTL_OPTIONS.map(opt => (
                <button 
                  key={opt.value} 
                  type="button" 
                  className={'biz-ttl-btn ' + (ttlHours === opt.value ? 'active' : '')}
                  onClick={() => setTtlHours(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* ТЕМА */}
          <div className="biz-form-group">
            <label>Выберите повод</label>
            <div className="biz-themes-grid">
              {THEMES.map(theme => (
                <div 
                  key={theme.id}
                  className={'biz-theme-card ' + (selectedTheme.id === theme.id ? 'selected' : '') + (theme.business ? ' business' : '')}
                  onClick={() => setSelectedTheme(theme)}
                >
                  <span className="biz-theme-emoji">{theme.emoji}</span>
                  <span className="biz-theme-title">{theme.title}</span>
                  {theme.business && <span className="biz-theme-badge">PRO</span>}
                </div>
              ))}
            </div>
          </div>

          <button type="submit" disabled={loading || !amount} className="biz-btn-create">
            {loading ? '⏳ Создание...' : bulkMode ? `📦 Создать ${bulkCount} подарков` : '🎁 Создать подарок'}
          </button>
        </form>
      )}
    </div>
  );
};

export default BusinessCreateVoucher;
